// Shift a decimal string's point by `places` (positive = right/multiply,
// negative = left/divide), as exact string manipulation rather than
// floating point arithmetic. `0.07 * 100` in JS is 7.000000000000001;
// shiftDecimal("0.07", 2) is exactly "7". Used anywhere a stored
// fraction (e.g. 0.07 for 7%) round-trips through an editable percent
// input -- DefaultsSection.tsx and OfferDetailsPanel.tsx both hit this,
// since plain *100/100 float math introduces noise for most decimals
// (e.g. typing "7" comes back as "7.000000000000001").
export function shiftDecimal(value: string, places: number): string {
  const trimmed = value.trim();
  const neg = trimmed.startsWith("-");
  let unsigned = neg ? trimmed.slice(1) : trimmed;

  // String(number) switches to exponential notation for very small/large
  // values (e.g. 1e-7). Normalize to plain decimal first so the digit
  // splitting below always sees an actual decimal string.
  const expMatch = unsigned.match(/^(\d*\.?\d*)e([+-]?\d+)$/i);
  if (expMatch) {
    const [, mantissa, expStr] = expMatch;
    const exp = parseInt(expStr, 10);
    const [mInt = "0", mFrac = ""] = mantissa.split(".");
    let digits = mInt + mFrac;
    let pointPos = mInt.length + exp;
    if (pointPos <= 0) {
      digits = "0".repeat(1 - pointPos) + digits;
      pointPos = 1;
    } else if (pointPos > digits.length) {
      digits = digits + "0".repeat(pointPos - digits.length);
    }
    unsigned = (digits.slice(0, pointPos) + "." + digits.slice(pointPos)).replace(/\.$/, "");
  }

  const [intPart = "0", fracPart = ""] = unsigned.split(".");

  let digits = intPart + fracPart;
  let pointPos = intPart.length + places;

  if (pointPos <= 0) {
    digits = "0".repeat(1 - pointPos) + digits;
    pointPos = 1;
  } else if (pointPos > digits.length) {
    digits = digits + "0".repeat(pointPos - digits.length);
  }

  let result = digits.slice(0, pointPos) + "." + digits.slice(pointPos);
  // Strip trailing fractional zeros, then a bare trailing dot, then leading zeros.
  result = result.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
  result = result.replace(/^0+(?=\d)/, "");
  if (result === "" || result === "-") result = "0";

  return neg ? "-" + result : result;
}

export function formatCurrency(value: number | null | undefined, opts?: { compact?: boolean }): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (opts?.compact) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(decimals)}%`;
}

export function formatMultiplier(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toFixed(2)}×`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(d);
}

export const DEAL_TYPE_LABELS: Record<string, string> = {
  cash_structure: "Cash",
  sba_structure: "SBA",
};

export const SOURCE_LABELS: Record<string, string> = {
  "Route Consultant": "Route Consultant",
  FXG: "FXG",
};
